/* eslint-disable no-unused-vars */
import React from 'react';
import InfoOutlined from '@material-ui/icons/InfoOutlined';
import { IGraphChromatographicDataProps } from 'components/molecules/PredictionGrid/components/ContainerGraphChromatographic/GraphChromatographic/types';
import { Fullscreen, FullscreenExit } from '@material-ui/icons';
import { postGraphicChromatographic } from 'services/prediction/grid-chromatographic';
import { useQuery } from 'react-query';
import { IContainerGraphChromatographicProps } from './types';
import GraphChromatographic from './GraphChromatographic';
import useStyles from './styles';

const SMALL_GRAPH_LIMIT = 6;
const BIG_GRAPH_LIMIT = 30;

const ContainerGraphChromatographic = (
  props: IContainerGraphChromatographicProps,
) => {
  const { serieNumber, startDate, endDate, toggleGraph, setToggleGraph } =
    props;

  const classes = useStyles();
  const [graphData, setGraphData] = React.useState<
    IGraphChromatographicDataProps[]
  >([]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const queryChromatographicGraph = useQuery({
    queryKey: ['queryChromatographicGraph', serieNumber, startDate, endDate],
    queryFn: async () => {
      const params = {
        serieNumber,
        startDate,
        endDate,
      };

      const { data } = await postGraphicChromatographic(params);

      data.forEach(dado => {
        const dataFormatadaParaOGrafico = new Date(dado.collectDate);
        dataFormatadaParaOGrafico.setDate(
          dataFormatadaParaOGrafico.getDate() + 1,
        );
        // eslint-disable-next-line no-param-reassign
        dado.collectDate = dataFormatadaParaOGrafico.toISOString();
      });
      return data;
    },
    onSuccess: data => {
      setGraphData(data);
    },
    enabled: true,
  });

  const isGraphDatesOk = (): boolean => Boolean(startDate) && Boolean(endDate);

  const isGraphSerieNumberOk = (): boolean =>
    Boolean(serieNumber) && serieNumber?.length === 1;

  const isGraphHaveFullData = (): boolean =>
    isGraphDatesOk() && isGraphSerieNumberOk();

  const isGraphEmpty = (): boolean => graphData && graphData?.length === 0;

  const isGraphSizeSmall = (): boolean =>
    graphData?.length <= SMALL_GRAPH_LIMIT;

  const isGraphBiggerThanLimit = (): boolean =>
    graphData?.length > BIG_GRAPH_LIMIT;

  const graphSizeSliced = (limit: number): IGraphChromatographicDataProps[] =>
    graphData.slice(0, limit);

  const showGraph = (): boolean =>
    isGraphHaveFullData() && !isGraphBiggerThanLimit() && !isGraphEmpty();

  const graphDataFormatted = (): IGraphChromatographicDataProps[] => {
    if (!toggleGraph && !isGraphBiggerThanLimit()) {
      return graphSizeSliced(SMALL_GRAPH_LIMIT);
    }

    return graphData;
  };

  const toggleFullscreenIcons = () =>
    toggleGraph ? (
      <FullscreenExit onClick={() => setToggleGraph(false)} />
    ) : (
      <Fullscreen onClick={() => setToggleGraph(true)} />
    );

  const graphNotOkText = () => {
    if (isGraphBiggerThanLimit()) {
      return `A quantidade de coletas a serem demonstradas no gráfico se limita em ${BIG_GRAPH_LIMIT} coletas, sendo assim reduza o período selecionado`;
    }

    if (isGraphEmpty() && isGraphHaveFullData()) {
      return 'Sem dados disponíveis';
    }

    return 'Para verificar a evolução, selecione um único número de série no filtro.';
  };

  React.useEffect(() => {
    if (!isGraphHaveFullData() || isGraphBiggerThanLimit()) {
      setToggleGraph(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGraphHaveFullData, isGraphBiggerThanLimit]);

  return (
    <>
      {!showGraph() && (
        <div className={classes.graphNotOk}>
          <div className={classes.graphNotOkContent}>
            <InfoOutlined aria-label="Informação" />
            <p>{graphNotOkText()}</p>
          </div>
        </div>
      )}
      {showGraph() && (
        <div className={classes.graphContainer}>
          <div className={classes.titleHeading}>
            <h3 className={classes.graphTitle}>Evolução Cromatográfica</h3>
            {!isGraphSizeSmall() && toggleFullscreenIcons()}
          </div>
          <GraphChromatographic {...{ data: [...graphDataFormatted()] }} />
        </div>
      )}
    </>
  );
};

export default ContainerGraphChromatographic;
